import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as ArrowRightIcon } from 'assets/icons/arrow-right.svg';
import { clsx } from 'clsx';
import Button from 'components/Button/Button';
import StatusBadgeWithSelector from 'components/StatusBadgeWithSelector/StatusBadgeWithSelector';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import { AppRoutes } from 'constants/AppRoutes';
import { BUTTON_VARIANT } from 'constants/interfaces';
import SimpleValueChainRow from 'pages/UserPages/Launchpad/components/RecentValueChainMaps/components/SimpleValueChainRow/SimpleValueChainRow';

import type IValueChainHeader from './interfaces/IValueChainHeader';

import styles from './sass/ValueChainHeader.module.scss';

// Simplified TopLeftControls: back button + VC name (doubles as the map selector) + read-only status.
function ValueChainHeader({ selectedMap, maps, onSelectMapId }: IValueChainHeader) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onClickOutside = (event: MouseEvent) => {
      if (!selectorRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [isOpen]);

  const handleSelect = (id: number) => {
    onSelectMapId(String(id));
    setIsOpen(false);
  };

  return (
    <div className={styles.root}>
      {selectedMap ? (
        <>
          <div className={styles.selector} ref={selectorRef}>
            <button
              type="button"
              className={styles.nameButton}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
              onClick={() => setIsOpen((open) => !open)}
            >
              <span className={styles.title}>{selectedMap.name}</span>
              <span className={clsx(styles.caret, isOpen && styles.caretOpen)} />
            </button>
            {isOpen ? (
              <div className={styles.menu}>
                <Button
                  className={styles.showAllButton}
                  title={t('launchpad.showAll')}
                  variant={BUTTON_VARIANT.openLink}
                  endIcon={<ArrowRightIcon />}
                  onClick={() => navigate(AppRoutes.valueChainMap)}
                />
                <div className={styles.list}>
                  <Table>
                    <TableBody>
                      {maps.map((map) => (
                        <SimpleValueChainRow
                          key={`hub_vc_${map.id}`}
                          valueChainItem={map}
                          onOpenValueChain={handleSelect}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : null}
          </div>
          <StatusBadgeWithSelector
            selectedStatus={selectedMap.status}
            onStatusChange={() => {}}
            archiveInfo={selectedMap?.latestArchivation || selectedMap?.latestStatus}
            isAccessDisabled
          />
        </>
      ) : null}
    </div>
  );
}

export default ValueChainHeader;
